/* eslint-disable @typescript-eslint/no-unused-vars */
import { AxiosResponse } from "axios";
import {
    GetAllWarehouseApiResponse,
    WarehouseStatusChangeResponse
} from "../../@types/DRO/GetAllWarehouseResponse";
import { erpAxios, getAuthToken } from "../axiosInstances";

// City API Response Types
interface City {
  cityId: string;
  cityName: string;
}

interface CityData {
  cityList: City[];
}

interface GetAllCityApiResponse {
  message: string;
  data: CityData;
  lastPage: boolean;
  hasNext: boolean;
}

// Warehouse Type API Response Types
interface WarehouseType {
  id: number;
  warehouseType: string;
}

interface WarehouseTypeData {
  warehouseTypeList: WarehouseType[];
}

interface GetAllWarehouseTypeApiResponse {
  message: string;
  data: WarehouseTypeData;
  hasNext: boolean;
  isLast: boolean;
}

// Franchise Admin API Response Types
export interface FranchiseAdminDto {
  userId: number;
  name: string;
}

interface GetFranchiseAdminsApiResponse {
  // code: string;
  message: string;
  data: FranchiseAdminDto[];
}

// Create Warehouse API Types
interface CreateWarehouseRequestBody {
  name: string;
  shortName: string;
  companyName: string;
  warehouseTypeId: number;
  cityId: string;
  isFranchiseHub: boolean;
  franchiseOwner: number | null;
  priceMultiplier: number | null;
}

interface CreateWarehouseApiResponse {
  message: string;
  data: string;
}

export class GetAllWarehouseListApi {
  public static api = new GetAllWarehouseListApi();
  private instance = erpAxios;

  async getAllWarehouseList(
    page: number = 0,
    pageSize: number = 10,
    search: string = "",
    myWarehouseOnly?: boolean,
    warehouseExceptMine?: boolean,
    enabled?: boolean
  ): Promise<GetAllWarehouseApiResponse> {
    try {
      const response: AxiosResponse<GetAllWarehouseApiResponse> =
        await this.instance.get("/erp/api/v1/warehouse/all", {
          headers: {
            Authorization: `Bearer ${this.getToken()}`,
          },
          params: {
            page,
            pageSize,
            search,
            myWarehouseOnly,
            warehouseExceptMine,
            enabled,
          },
        });
      return response.data;
    } catch (error) {
      console.error("Error fetching vendor data", error);
      throw error;
    }
  }

  async getAllCityList(
    page: number = 0,
    size: number = 10
  ): Promise<GetAllCityApiResponse> {
    try {
      const response: AxiosResponse<GetAllCityApiResponse> =
        await this.instance.get("/erp/api/v1/city", {
          headers: {
            Authorization: `Bearer ${this.getToken()}`,
          },
          params: {
            page,
            size,
          },
        });
      return response.data;
    } catch (error) {
      console.error("Error fetching city data", error);
      throw error;
    }
  }

  async getAllWarehouseType(
    page: number = 0,
    size: number = 10
  ): Promise<GetAllWarehouseTypeApiResponse> {
    try {
      const response: AxiosResponse<GetAllWarehouseTypeApiResponse> =
        await this.instance.get("/erp/api/v1/warehouse/type", {
          headers: {
            Authorization: `Bearer ${this.getToken()}`,
          },
          params: {
            page,
            size,
          },
        });
      return response.data;
    } catch (error) {
      console.error("Error fetching warehouse type data", error);
      throw error;
    }
  }

  async getFranchiseAdmins(): Promise<GetFranchiseAdminsApiResponse> {
    try {
      const response: AxiosResponse<GetFranchiseAdminsApiResponse> =
        await this.instance.get("/erp/user/get/franchise-admins", {
          headers: {
            Authorization: `Bearer ${this.getToken()}`,
          },
        });
      return response.data;
    } catch (error) {
      console.error("Error fetching franchise admins", error);
      throw error;
    }
  }

  async createWarehouse(
    warehouseData: CreateWarehouseRequestBody
  ): Promise<CreateWarehouseApiResponse> {
    try {
      const token = this.getToken();
      if (!token) {
        throw new Error("No authentication token found");
      }

      const response: AxiosResponse<CreateWarehouseApiResponse> =
        await this.instance.post("/erp/api/v1/warehouse/add", warehouseData, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
      return response.data;
    } catch (error: any) {
      console.error("Error creating warehouse", error);
      if (error.response) {
        console.error("Response status:", error.response.status);
        console.error("Response data:", error.response.data);
      }
      throw error;
    }
  }

  // Toggles a warehouse's enabled/disabled status.
  // PUT /erp/api/v1/warehouse/{id}/status-change — no request body.
  async changeWarehouseStatus(
    id: number
  ): Promise<WarehouseStatusChangeResponse> {
    try {
      const response: AxiosResponse<WarehouseStatusChangeResponse> =
        await this.instance.put(
          `/erp/api/v1/warehouse/${id}/status-change`,
          {},
          {
            headers: {
              Authorization: `Bearer ${this.getToken()}`,
            },
          }
        );
      return response.data;
    } catch (error) {
      console.error("Error changing warehouse status", error);
      throw error;
    }
  }

  getToken() {
    return getAuthToken();
  }
}